import _mergeWith from 'lodash/mergeWith';
import { fromEntries } from 'utils/core';

export function isObject(obj) {
    return Object(obj) === obj;
}

export function isEmptyObject(obj) {
    return Object.keys(obj).length === 0;
}

export function isJSONParsable(str = '') {
    try {
        JSON.parse(str);
    } catch (err) {
        return false;
    }
    return true;
}

export function parseJSON_ifParsable(target = '', fallbackVal = target) {
    try {
        return JSON.parse(target);
    } catch (err) {
        return fallbackVal;
    }
}

export function objFirstLevelJSONStringify(obj = {}) {
    // 這個用來專門刪除 setting key 的 key name: "deleted" 比較特別
    // 值需要維持是 array 的型別來給後端做處理
    const DELETED_KEY = 'deleted';

    return Object.entries(obj).reduce((newObj, [key, val]) => {
        // 因除了它以外的資料都是要被儲存下來的，因此需要 stringify 才能儲存
        val = isObject(val) && key !== DELETED_KEY ? JSON.stringify(val) : val;
        newObj[key] = val;

        return newObj;
    }, {});
}

export function objFirstLevelJSONParse(obj = {}) {
    return Object.entries(obj).reduce((newObj, [key, val]) => {
        val = parseJSON_ifParsable(val);
        newObj[key] = val;

        return newObj;
    }, {});
}

export const reduxObjKeysUpdateActions = {
    MERGE: 'merge',
    REPLACE: 'replace'
};
export function updateReduxObjKeyHandleDelete(
    currentState,
    newState,
    updateAction = reduxObjKeysUpdateActions.MERGE
) {
    const currentDeletedKeyArray = currentState?.deleted ? [...currentState.deleted] : [];
    const newDeletedKeyArray = currentDeletedKeyArray.filter(
        deletedKey => newState[deletedKey] === undefined
    );
    const nextState =
        updateAction === reduxObjKeysUpdateActions.MERGE
            ? _mergeWith(
                  {},
                  currentState,
                  newState,
                  { deleted: newDeletedKeyArray },
                  (_, srcVal) => {
                      if (Array.isArray(srcVal)) {
                          return srcVal;
                      }
                  }
              )
            : { ...currentState, ...newState, deleted: newDeletedKeyArray };

    return nextState;
}

export function deleteReduxObjKey(state = {}, deleted = []) {
    const newState = { ...state };
    const newDeletedSet = Array.isArray(state.deleted) ? new Set([...state.deleted]) : new Set([]);

    deleted.forEach(wantToDeleteKeyName => {
        if (newState[wantToDeleteKeyName] !== undefined) {
            delete newState[wantToDeleteKeyName];
            newDeletedSet.add(wantToDeleteKeyName);
        }
    });

    return {
        ...newState,
        deleted: [...newDeletedSet]
    };
}

// Match prefix or suffix in a key
const matchKey = (key, { prefixes = [], suffixes = [] }) =>
    prefixes.some(prefix => key.startsWith(prefix)) ||
    suffixes.some(suffix => key.endsWith(suffix));

// Recursive API response transformer
const typeConversion = (
    key,
    value,
    { prefixes = ['enable'], suffixes = ['Id', 'id'], Constructor = Number }
) => {
    if (typeof value === 'string' && matchKey(key, { prefixes, suffixes })) {
        const convertedValue = Constructor(value);
        return !isNaN(convertedValue) ? { [key]: convertedValue } : { [key]: value };
    }
    return { [key]: value };
};

// Recursive API response transformer
export const transformApiResponse = (apiResp = {}, options = {}) => {
    const transform = obj =>
        Object.entries(obj).reduce((acc, [key, value]) => {
            acc[key] =
                typeof value === 'object' && value !== null
                    ? transform(value)
                    : typeConversion(key, value, options)[key];
            return acc;
        }, {});
    return transform(apiResp);
};

export function reverseObj(obj) {
    return fromEntries(Object.entries(obj).map(([key, value]) => [value, key]));
}

export function isNonEmptyObject(value) {
    return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        Object.keys(value).length > 0 // Check if it has at least one own enumerable property
    );
}

/**
 * 檢查指定的登入方式是否在資料陣列中有設定值
 * @example
 * // data = [{line:{enterprise:'maac'}},{facebook:{enterprise:'maac'}}]
 * // loginMethod = 'line'
 * // returns [true,false]
 * @param {Array} data - 包含登入方式設定的物件陣列
 * @param {string} loginMethod - 要檢查的登入方式 (例如: 'line', 'enterprise')
 * @returns {Array<boolean>} 布林值陣列，表示每個項目是否有設定該登入方式
 */
export const isEnableLoginMethodObject = (data = [], loginMethod) => {
    return data.map(value => {
        if (!value) return false;

        const parseValue = parseJSON_ifParsable(value);
        if (Boolean(parseValue[loginMethod])) {
            return true;
        }
        return false;
    });
};

/**
 * 檢查指定的登入方式是否在資料陣列中有設定值
 * @example
 * // data = {line:{enterprise:'maac'}
 * // loginMethod = 'line'
 * // settingLoginMethod = 'enterprise'
 * // returns {enterprise:'maac'}
 * @param {Object} data - 包含登入方式設定的物件
 * @param {string} loginMethod - 指定登入方式 (例如: 'line', 'enterprise')
 * @param {string} settingLoginMethod - 指定登入方式要使用的對應的登入方式 (例如: 'line', 'enterprise')
 * @returns {Object} 包含登入方式設定的物件
 */
export const getLoginMethodObjectValue = (data, loginMethod, settingLoginMethod) => {
    if (!data) return undefined;

    const parseValue = parseJSON_ifParsable(data);
    return parseValue?.[loginMethod]?.[settingLoginMethod];
};

/*
    Convert a string to a nested object
    @example
    str = 'a.b.c'
    value = {a:{b:{c:1}}}
    return {a:{b:{c:1}}}
    @param {string} str - The string to convert
    @param {Object} value - The value to convert
    @returns {Object} The nested object
*/
export const stringToNestedObject = (str, value = {}) => {
    const keys = str.split(/[,.]/).map(k => k.trim());
    return keys.reduceRight((acc, key) => ({ [key]: acc }), value);
};

/**
 * 根據路徑字串建立巢狀物件結構
 * @param {string} path - 路徑字串（例如：'a.b.c' 或 'a,b,c'）
 * @param {*} value - 要設定的值
 * @returns {Object} 巢狀物件結構
 */
export function createNestedObject(path, value) {
    if (!path) return {};

    const keys = path
        .split(/[,.]/)
        .map(k => k.trim())
        .filter(Boolean);

    if (keys.length === 0) return {};

    return keys.reduceRight((acc, key) => ({ [key]: acc }), value);
}

/**
 * 深度合併兩個對象
 *
 * 與普通的 {...target, ...source} 展開運算符不同，deepMerge 會遞歸地合併嵌套對象，
 * 而不是直接覆蓋整個子對象。
 *
 * @param {Object} target - 目標對象
 * @param {Object} source - 來源對象
 * @returns {Object} 合併後的對象（返回新對象，不會修改原始對象）
 *
 * @example
 * 使用展開運算符（淺合併）- 子對象會被完全覆蓋
 * const obj1 = { a: { b: 1, c: 2 } };
 * const obj2 = { a: { d: 3 } };
 * const shallow = { ...obj1, ...obj2 };
 * 結果: { a: { d: 3 } } ❌ b 和 c 丟失
 *
 * 使用 deepMerge（深度合併）- 子對象會被遞歸合併
 * const deep = deepMerge(obj1, obj2);
 * 結果: { a: { b: 1, c: 2, d: 3 } } ✓ 所有屬性都保留
 *
 * @example
 * 實際使用場景：合併多個 Redux 狀態路徑
 * const result = {};
 * const nested1 = createNestedObject('settingGameStyle.unregisterKeyVisionSettings.bgColor', '#fff');
 * const nested2 = createNestedObject('settingGameStyle.registeredKeyVisionSettings.bgColor', '#000');
 *
 * result = deepMerge(result, nested1);
 * result = deepMerge(result, nested2);
 * 結果: {
 *   settingGameStyle: {
 *     unregisterKeyVisionSettings: { bgColor: '#fff' },
 *     registeredKeyVisionSettings: { bgColor: '#000' }
 *   }
 * }
 */
export function deepMerge(target, source) {
    const result = { ...target };

    for (const key in source) {
        if (source.hasOwnProperty(key)) {
            if (
                source[key] &&
                typeof source[key] === 'object' &&
                !Array.isArray(source[key]) &&
                target[key] &&
                typeof target[key] === 'object' &&
                !Array.isArray(target[key])
            ) {
                // 遞迴合併嵌套對象，保留兩邊的屬性
                result[key] = deepMerge(target[key], source[key]);
            } else {
                // 直接賦值（基本類型、數組、或目標中不存在該 key）
                result[key] = source[key];
            }
        }
    }

    return result;
}
